/**
 * Company stamps and signatures stored in Supabase (company_document_assets + Storage bucket company-assets).
 * Use for PDF/document generation across the platform.
 */
import { supabase } from './supabase';

const BUCKET = 'company-assets';
const SIGNED_URL_EXPIRY_SEC = 3600; // 1 hour for PDF generation

/**
 * Get a signed URL for a storage path in the company-assets bucket.
 * @param {string} storagePath - Path within bucket (e.g. "user-id/stamp/asset-id.png")
 * @returns {Promise<string|null>}
 */
export async function getSignedAssetUrl(storagePath) {
  if (!storagePath?.trim()) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath.trim(), SIGNED_URL_EXPIRY_SEC);
  if (error) {
    console.warn('[companyAssets] getSignedAssetUrl', error);
    return null;
  }
  return data?.signedUrl ?? null;
}

/**
 * Fetch default stamp (from Settings) and signature (from the current user's profile) for use in PDFs/documents.
 *
 * - Stamp: still managed in Settings → General (company_document_assets, type='stamp').
 * - Signature: taken from the logged-in user's profile.signature_data (drawn in Team → Member Details).
 *
 * @returns {Promise<{ stampUrl: string|null, signatureUrl: string|null }>}
 */
export async function getDefaultStampAndSignature() {
  let stampUrl = null;

  // Load default company stamp exactly as before.
  try {
    const { data: rows, error } = await supabase
      .from('company_document_assets')
      .select('type, storage_path')
      .eq('is_default', true);
    if (error) {
      console.warn('[companyAssets] getDefaultStampAndSignature stamp query', error);
    } else {
      const stamp = (rows || []).find((r) => r.type === 'stamp');
      if (stamp?.storage_path) {
        stampUrl = await getSignedAssetUrl(stamp.storage_path);
      }
    }
  } catch (err) {
    console.warn('[companyAssets] getDefaultStampAndSignature stamp error', err);
  }

  // Signature now comes from the current user's profile.signature_data (data URL).
  let signatureUrl = null;
  try {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError) {
      console.warn('[companyAssets] getDefaultStampAndSignature auth error', authError);
    } else if (authData?.user?.id) {
      const userId = authData.user.id;
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('signature_data')
        .eq('id', userId)
        .single();
      if (profileError) {
        console.warn('[companyAssets] getDefaultStampAndSignature profile error', profileError);
      } else if (profile?.signature_data) {
        signatureUrl = profile.signature_data;
      }
    }
  } catch (err) {
    console.warn('[companyAssets] getDefaultStampAndSignature signature error', err);
  }

  return { stampUrl, signatureUrl };
}
